import { useCallback, useEffect, useState } from 'react';
import type { MapGeoJSONFeature } from 'maplibre-gl';
import { useMapStore } from './MapContext';
import type { LandCategory, RenderedParcelSummary } from './types';

const PARCEL_RENDER_LAYERS = ['parcels-fill', 'parcels-line'];

function toNullableString(value: unknown): string | null {
  const normalized = String(value ?? '').trim();
  return normalized || null;
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toRenderedParcelSummary(feature: MapGeoJSONFeature): RenderedParcelSummary | null {
  const props = feature.properties || {};
  const id = toNullableString(props._id ?? feature.id);
  if (!id) return null;

  return {
    id,
    feature_name: toNullableString(props.feature_name ?? props._feature_name),
    external_id: toNullableString(props.external_id ?? props._external_id),
    sg_number: toNullableString(props.sg_number ?? props._sg_number),
    erf_number: toNullableString(props.erf_number ?? props._erf_number),
    farm_name: toNullableString(props.farm_name ?? props._farm_name),
    province: toNullableString(props.province),
    municipality: toNullableString(props.municipality),
    local_municipality: toNullableString(props.local_municipality),
    district_municipality: toNullableString(props.district_municipality),
    prcl_key: toNullableString(props.prcl_key),
    land_category: (toNullableString(props.land_category) as LandCategory | null),
    land_type: toNullableString(props.land_type ?? props._land_type),
    country_code: toNullableString(props.country_code),
    admin_level_1: toNullableString(props.admin_level_1),
    admin_level_2: toNullableString(props.admin_level_2),
    natural_key_value: toNullableString(props.natural_key_value),
    area_ha: toNumber(props.area_ha ?? props._area_ha, 0),
    linked_entity_id: toNullableString(props.linked_entity_id),
  };
}

export function useRenderedParcelSummaries(options: { enabled?: boolean; limit?: number } = {}) {
  const { enabled = true, limit } = options;
  const map = useMapStore((state) => state.map);
  const [parcels, setParcels] = useState<RenderedParcelSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!map || !enabled) {
      setParcels([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }
    if (!map.isStyleLoaded()) return;

    const layers = PARCEL_RENDER_LAYERS.filter((layerId) => Boolean(map.getLayer(layerId)));
    if (layers.length === 0) {
      setParcels([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    const seen = new Map<string, RenderedParcelSummary>();
    const rendered = map.queryRenderedFeatures(undefined, { layers });
    for (const feature of rendered) {
      const summary = toRenderedParcelSummary(feature);
      if (!summary || seen.has(summary.id)) continue;
      seen.set(summary.id, summary);
    }

    const uniqueParcels = Array.from(seen.values());
    setTotal(uniqueParcels.length);
    setParcels(typeof limit === 'number' ? uniqueParcels.slice(0, Math.max(0, limit)) : uniqueParcels);
    setIsLoading(false);
  }, [enabled, limit, map]);

  useEffect(() => {
    if (!map || !enabled) {
      setParcels([]);
      setTotal(0);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    const frame = window.requestAnimationFrame(() => {
      refresh();
    });

    const handleMapChange = () => {
      refresh();
    };

    map.on('idle', handleMapChange);
    map.on('moveend', handleMapChange);
    map.on('styledata', handleMapChange);

    return () => {
      window.cancelAnimationFrame(frame);
      map.off('idle', handleMapChange);
      map.off('moveend', handleMapChange);
      map.off('styledata', handleMapChange);
    };
  }, [enabled, map, refresh]);

  return {
    parcels,
    total,
    isLoading,
  };
}
